import React, {useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ParentStepReference, StepReference} from 'pipelines/network/PipelineDAO';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {DataReference} from '../main-view/MainViewTypes';

export const FRONTEND_MAX_COMMENT_LENGTH = 1500;

export interface FlagRowsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onFinalizeFlagging?: () => void;
  analysis: AnalysisGraphQL;
  selectedRowIds: Set<string>;
  flaggedRowIds: Set<string>;
  isAddingFlag: boolean; // if false we clear the flags
  parentStepReference: ParentStepReference;
  outputDataReferences?: Array<DataReference>;
}

export function openFlagRowsModal(
  flagRowsModalProps: Omit<FlagRowsModalProps, 'onRequestClose' | 'onRequestSubmit'>
): void {
  runModal(FlagRowsModal, flagRowsModalProps);
}

function assertAndConvertToInteger(value: string): number {
  const maybeNumber = parseInt(value, 10);
  assert(!Number.isNaN(maybeNumber), `Expected a number but got ${value}`);
  return maybeNumber;
}

export function FlagRowsModal(props: FlagRowsModalProps): JSX.Element {
  const commentFormInput = useFormInput('');
  const comment = commentFormInput.value;
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const reasonOptions = props.isAddingFlag
    ? BenchlingConfig.ANALYSIS_TOOL_FLAGGING_REASON_OPTIONS
    : BenchlingConfig.ANALYSIS_TOOL_UNFLAGGING_REASON_OPTIONS;
  const hasReasonOptions = reasonOptions.length > 0;

  const hasValidComment = comment && comment.match(/^\s*$/) == null;
  const canSubmit = hasValidComment && (!hasReasonOptions || selectedReason != null);
  // We do not want to add a remove flags comment on rows that are not previously flagged
  const selectedRowIdArray = props.isAddingFlag
    ? [...props.selectedRowIds]
    : [...props.selectedRowIds].filter((selectedRowId) => props.flaggedRowIds.has(selectedRowId));

  async function handleSubmit(): Promise<void> {
    const rowIndices = selectedRowIdArray.map((selectedRowId: string) =>
      assertAndConvertToInteger(selectedRowId)
    );
    const flaggingInfo = {
      isFlagged: props.isAddingFlag,
      reason: hasReasonOptions ? selectedReason : null,
      comment,
      rowIndices,
    };
    assert(
      props.parentStepReference.stepReference.type === StepReference.STEP_ID,
      'Expected StepId in FlagRowsModal'
    );
    const parentStep = getLegacyStepFromAnalysis(
      props.analysis,
      props.parentStepReference.stepReference.stepId
    );

    props.onRequestSubmit();

    if (parentStep.definition.stepType === PipelineStepType.FLAG_ROWS) {
      const previousStepConfig = getPipelineStepConfig(parentStep, PipelineStepType.FLAG_ROWS, 0);
      const stepConfig = {
        flaggingInfoList: [...previousStepConfig.flaggingInfoList, flaggingInfo],
      };
      await withLoadingScreen(
        AnalysisActions.updateAndRunAnalysisStep(props.analysis.id, {stepId: parentStep.id, stepConfig})
      );
    } else {
      const outputDataReferences = props.outputDataReferences
        ? {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: props.outputDataReferences}}
        : {};
      const stepConfig = {
        flaggingInfoList: [flaggingInfo],
      };
      await withLoadingScreen(
        AnalysisActions.addAndRunAnalysisStepsV2(props.analysis.id, [
          {
            step: {
              stepType: PipelineStepType.FLAG_ROWS,
              stepVersion: 0,
              stepConfig,
              stepInputParents: {
                [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [props.parentStepReference],
              },
              ...outputDataReferences,
            },
          },
        ])
      );
    }

    props.onFinalizeFlagging?.();
  }

  const flagNoun = pluralize(selectedRowIdArray.length, 'flag', 'flags');
  const rowNoun = pluralize(selectedRowIdArray.length, 'row', 'rows');
  return (
    <Modal
      title={
        props.isAddingFlag
          ? `Add flag to ${selectedRowIdArray.length} ${rowNoun}`
          : `Remove ${selectedRowIdArray.length} ${flagNoun}`
      }
      buttons={[{style: ButtonStyle.SECONDARY, submit: true, text: 'Save', disabled: !canSubmit}]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      expandHeight={true}
      dataTestAttrs={{component: 'FlagRowsModal', element: 'root'}}
    >
      {!props.isAddingFlag && <p>{`Write a comment to remove ${flagNoun}.`}</p>}
      {hasReasonOptions && (
        <FormGroup>
          <InputWrapper id="flagRowsModalReason" label="Reason" labelStyle={FieldLabel.STYLES.LABEL}>
            <SelectDropdown
              displayName={selectedReason ?? 'Select a reason'}
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                  {reasonOptions.map((reason: string) => (
                    <Option
                      key={reason}
                      onSelect={() => {
                        setSelectedReason(reason);
                      }}
                      value={reason}
                      dataTestAttrs={{
                        component: 'FlagRowsModal',
                        element: 'reasonOption',
                        key: reason,
                      }}
                    >
                      {reason}
                    </Option>
                  ))}
                </Menu>
              }
              dataTestAttrs={{
                component: 'FlagRowsModal',
                element: 'reasonDropdown',
              }}
            />
          </InputWrapper>
        </FormGroup>
      )}
      <FormGroup>
        <InputWrapper id="flagRowsModalComment" label="Comment" labelStyle={FieldLabel.STYLES.LABEL}>
          <textarea
            maxLength={FRONTEND_MAX_COMMENT_LENGTH}
            onChange={(e) => {
              // @ts-expect-error
              commentFormInput.onChange(e);
            }}
            rows={2}
            value={comment}
          />
        </InputWrapper>
      </FormGroup>
    </Modal>
  );
}
